import discountQueries from '@apis/queries/discountQueries';
import useToast from '@hooks/ui/useToast';
import { DiscountCouponRow } from '@models/discount';
import { OrderDiscount } from '@models/order';
import orderWithTotalAmount from '@providers/OrderProvider/utils/orderWithTotalAmount';
import { useFlow } from '@stackflow/react';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { orderActions } from '@store/order/orderSlice';
import { useQuery } from '@tanstack/react-query';
import { getOrderCouponDiscountAmount, toItemCouponDiscount } from '@utils/couponUtils';
import { makeItemKey } from '@utils/orderUtils';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ItemCouponTarget } from '../atoms';

/**
 * 쿠폰 적용.
 * 상품 쿠폰과 주문(장바구니) 쿠폰은 함께 쓸 수 없고, 쿠폰을 적용하면 다른 할인 수단(선물·선불카드·포인트·임직원 할인)을 초기화한다.
 */
function useApplyCoupons() {
    const { t } = useTranslation('order');
    const toast = useToast();
    const { pop } = useFlow();
    const dispatch = useDispatch();
    const state = useCalculatedRequest();
    const {
        discounts, giftPayments, prepaidCardPayments, useEmployeeDiscount, orderLines, shopId,
    } = state;
    const { data: discountResponse } = useQuery({
        ...discountQueries.list(shopId),
        enabled: !!shopId,
    });

    const hasOtherDiscounts = (scope: 'ITEM' | 'ORDER') => {
        // 반대쪽 쿠폰, 포인트, 임직원 할인, 선물, 선불카드
        const otherDiscountExists = !!discounts?.find((it) => (it.type === 'COUPON' && it.discountScope !== scope)
            || it.type === 'POINT' || it.type === 'EMPLOYEE');
        return otherDiscountExists || useEmployeeDiscount
            || (giftPayments?.length || 0) > 0 || (prepaidCardPayments?.length || 0) > 0;
    };

    const clearOthers = (scope: 'ITEM' | 'ORDER') => {
        if (hasOtherDiscounts(scope)) {
            toast.info(t('couponPage.resetOthers'));
            dispatch(orderActions.clearDiscounts());
        }
    };

    /** 상품 쿠폰 초안을 할인 내역으로 */
    const getItemDiscounts = (draft: Record<string, ItemCouponTarget>): OrderDiscount[] => Object.entries(draft)
        .map(([couponId, target]) => {
            const coupon = discountResponse?.coupons.find((it) => it.id === couponId);
            const line = orderLines.find((it) => makeItemKey(it) === target.itemKey);
            return coupon && line ? toItemCouponDiscount(coupon, line, target.units[0]) : undefined;
        })
        .filter((it): it is OrderDiscount => !!it);

    const getOrderDiscountAmount = (coupon: DiscountCouponRow) => {
        const calculatedTotal = orderWithTotalAmount({
            ...state, discounts: [],
        }, discountResponse).totalAmount;
        return getOrderCouponDiscountAmount(coupon, calculatedTotal);
    };

    const applyItemCoupons = (draft: Record<string, ItemCouponTarget>) => {
        const itemDiscounts = getItemDiscounts(draft);
        if (itemDiscounts.length === 0) {
            // 상품 쿠폰만 해제한다
            dispatch(orderActions.setDiscounts(
                discounts?.filter((it) => !(it.type === 'COUPON' && it.discountScope === 'ITEM')) || [],
            ));
            pop();
            return;
        }
        clearOthers('ITEM');
        dispatch(orderActions.setDiscounts(itemDiscounts));
        pop();
    };

    const applyOrderCoupon = (coupon?: DiscountCouponRow) => {
        if (!coupon) {
            dispatch(orderActions.setDiscounts([]));
            pop();
            return;
        }
        clearOthers('ORDER');
        dispatch(orderActions.setDiscounts([{
            type: 'COUPON',
            couponId: coupon.id,
            couponName: coupon.title,
            discountScope: 'ORDER',
            discountAmount: getOrderDiscountAmount(coupon),
            discountMethod: coupon.discountMethod,
        }]));
        pop();
    };

    /** 선택한 쿠폰이 없을 때. 적용되어 있던 쿠폰만 해제한다 */
    const clearCoupons = () => {
        dispatch(orderActions.setDiscounts(discounts?.filter((it) => it.type !== 'COUPON') || []));
        pop();
    };

    return {
        getItemDiscounts,
        getOrderDiscountAmount,
        applyItemCoupons,
        applyOrderCoupon,
        clearCoupons,
    };
}

export default useApplyCoupons;
